import React, { useEffect, useState } from 'react';
import { Box, CircularProgress } from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { useBusiness } from '../contexts/BusinessContext';
import SubscribeToContinue from '../pages/SubscribeToContinue';

interface BusinessSubscription {
  status: string;
  dateEnd: string | null;
}

type GateStatus = 'loading' | 'active' | 'inactive';

const isCurrentlyActive = (sub: BusinessSubscription) =>
  sub.status === 'Active' && (sub.dateEnd === null || new Date(sub.dateEnd) > new Date());

// Mirrors the backend's SubscriptionGateFilter — admins bypass, everyone else
// needs an active subscription on their currently-selected business. Routes
// wrapped here don't map 1:1 to the backend's [SubscriptionExempt] allowlist;
// only the "core operational" pages (dashboard, devices, contacts) are gated
// client-side, since the exempt pages (Businesses/My Business/Admin/Settings)
// must stay reachable for the join/subscribe flow itself.
const RequireActiveSubscription: React.FC<{ children: React.ReactElement }> = ({ children }) => {
  const auth = useAuth();
  const { selectedBusinessId, isLoading: businessLoading } = useBusiness();
  const [status, setStatus] = useState<GateStatus>('loading');

  useEffect(() => {
    if (auth.hasRole('DMO_ADMIN')) {
      setStatus('active');
      return;
    }
    if (businessLoading) {
      return;
    }
    if (!selectedBusinessId) {
      setStatus('inactive');
      return;
    }

    let cancelled = false;
    setStatus('loading');
    fetch(`/api/businesses/${selectedBusinessId}/subscriptions`, {
      headers: { Authorization: `Bearer ${auth.tokens?.accessToken}` },
    })
      .then((res) => (res.ok ? res.json() : []))
      .then((subs: BusinessSubscription[]) => {
        if (cancelled) return;
        setStatus(subs.some(isCurrentlyActive) ? 'active' : 'inactive');
      })
      .catch(() => {
        if (!cancelled) setStatus('inactive');
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth.user, selectedBusinessId, businessLoading]);

  if (status === 'loading') {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  if (status === 'inactive') {
    return <SubscribeToContinue />;
  }

  return children;
};

export default RequireActiveSubscription;
